<div>
  <div class="d-flex justify-content-between align-items-center margin-bottom-md flex-wrap gap-2">
    <h2 id="dp-details-heading" class="m-0" [class.h4]="digitalAssetFormUpdateFeatureEnabled">
      {{ headingText | translate }}
    </h2>
    @if (displayDigitalAssetDetailsHelpLink) {
      <ds-link [external]="true" [href]="digitalAssetDetailsHelpLinkUrl" data-test-id="digital-asset-details-help-link">
        {{ 'label_digital_property_details_learn_more' | translate }}
      </ds-link>
    }
  </div>
  @if (displayRequiredFieldsLabel) {
    <div class="margin-bottom-md" data-test-id="required-fields-label-form">{{ 'required_fields_label' | translate }}</div>
  }

  @if (selectedWorkspace || disableWorkspaceValidation) {
    <form [formGroup]="form">
      <fieldset aria-labelledby="dp-details-heading">
        @if (shouldDisplayWorkspaceName) {
          <div class="margin-bottom d-flex">
            <div class="d-flex flex-column align-items-start" id="workspace-name">
              <span class="label margin-bottom-xs">{{ 'label_workspace_name' | translate }}</span>
              {{ selectedWorkspace.name }}
            </div>
          </div>
        }
        @if (digitalAssetFormUpdateFeatureEnabled) {
          <div class="row margin-bottom">
            <div class="col-12">
              <ds-input
                [id]="$digitalProperty.name"
                [label]="'digital_name_label' | translate"
                [required]="canEditName !== false"
                [showRequiredAsterisk]="canEditName !== false"
                [errorMessage]="nameError$ | async"
                [errorState]="!!(nameError$ | async)"
                data-test-id="digital-property-name-ds-input"
              >
                <input
                  ds-input-field
                  type="text"
                  [formControlName]="$digitalProperty.name"
                  [maxlength]="charLimits[$digitalProperty.name]"
                />
              </ds-input>
            </div>
          </div>

          <div class="row margin-bottom">
            <div class="col-12">
              <ds-input
                [id]="$digitalProperty.propertyKey"
                [label]="'digital_property_key_label' | translate"
                [instructions]="!digitalProperty ? ('digital_property_key_description' | translate) : null"
                [required]="!digitalProperty"
                [showRequiredAsterisk]="!digitalProperty"
                [state]="propertyKeyInputState"
                [errorMessage]="propertyKeyError$ | async"
                [errorState]="!!(propertyKeyError$ | async)"
                data-test-id="digital-property-key-ds-input"
              >
                <input
                  ds-input-field
                  type="text"
                  [formControlName]="$digitalProperty.propertyKey"
                  [maxlength]="!digitalProperty ? charLimits[$digitalProperty.propertyKey] : -1"
                />
              </ds-input>
            </div>
          </div>
        } @else {
          <div class="row margin-bottom">
            <app-common-input
              class="col-12"
              [formValidationRequest$]="formValidationRequest$"
              [form]="form"
              [context]="{
                label: 'digital_name_label',
                field: $digitalProperty.name,
                disabled: canEditName === false,
              }"
            ></app-common-input>
          </div>

          <div class="row margin-bottom">
            <app-common-input
              class="col-12"
              [formValidationRequest$]="formValidationRequest$"
              [form]="form"
              [context]="{
                label: 'digital_property_key_label',
                field: $digitalProperty.propertyKey,
                disabled: !!digitalProperty,
              }"
            >
            </app-common-input>
          </div>
        }

        <app-common-select
          class="d-block margin-bottom"
          [formValidationRequest$]="formValidationRequest$"
          [values]="digitalPropertyTypes"
          [form]="form"
          [useDefaultAsteriskColor]="digitalAssetFormUpdateFeatureEnabled"
          [context]="{
            label: 'digital_type_label',
            field: $digitalProperty.typeId,
            valueKey: '_id',
            nameKey: $digitalProperty.name,
            disabled: (digitalProperty | notNullish) || canEditProperty === false,
          }"
        ></app-common-select>

        @if (tagsFeatureEnabled) {
          <app-multiselect-typeahead
            label="tags"
            formControlName="tags"
            [form]="form"
            [allowCustomValues]="true"
            [maxValues]="maxDigitalTag"
            [maxValueLength]="maxDigitalTagCharLength"
            [description]="
              (digitalAssetFormUpdateFeatureEnabled ? 'tags_description_filtering_management' : 'type_and_press_enter_tag')
                | translate
            "
            [isAlphanumericValidationEnabled]="true"
          ></app-multiselect-typeahead>
        }

        @if (isWebsite) {
          <app-common-select
            class="d-block margin-bottom"
            [formValidationRequest$]="formValidationRequest$"
            [values]="auditToolSelectOptions"
            [form]="form"
            [useDefaultAsteriskColor]="digitalAssetFormUpdateFeatureEnabled"
            [context]="{
              label: 'digital_default_tool_label',
              field: $digitalProperty.defaultTool,
              valueKey: 'value',
              nameKey: 'displayName',
              disabled: canEditTool === false,
              tooltip: 'digital_default_tool_tooltip',
            }"
          ></app-common-select>

          <app-common-input
            class="d-block margin-bottom"
            [formValidationRequest$]="formValidationRequest$"
            [form]="form"
            [useDefaultAsteriskColor]="digitalAssetFormUpdateFeatureEnabled"
            [context]="{
              label: 'digital_url_label',
              field: $digitalProperty.url,
              description: 'digital_url_description',
              disabled: canEditProperty === false,
            }"
          ></app-common-input>

          <ng-container *ngTemplateOutlet="domainRestrictionSettings; context: { form: form }"></ng-container>
          <div>
            <div id="contain-accessibility-statement" class="label margin-bottom-xs">
              {{ 'contain_digital_accessibility_statement_label' | translate }}
            </div>
            <div class="row">
              <div class="col-12">
                <div
                  class="d-flex flex-column align-items-start btn-group"
                  role="radiogroup"
                  aria-labelledby="contain-accessibility-statement"
                >
                  <label class="clean-button button-no btn border-0" [class.active]="hasAccessibilityStatement === false">
                    <input
                      type="radio"
                      data-test-id="accessibility-statement-no"
                      [(ngModel)]="hasAccessibilityStatement"
                      [ngModelOptions]="{ standalone: true }"
                      [value]="false"
                      (ngModelChange)="onAccessibilityStatementSelectorChange($event)"
                    />
                    {{ 'no' | translate }}
                  </label>
                  <label class="clean-button button-yes btn border-0" [class.active]="hasAccessibilityStatement === true">
                    <input
                      type="radio"
                      data-test-id="accessibility-statement-yes"
                      [(ngModel)]="hasAccessibilityStatement"
                      [ngModelOptions]="{ standalone: true }"
                      [value]="true"
                      (ngModelChange)="onAccessibilityStatementSelectorChange($event)"
                    />
                    {{ 'yes' | translate }}
                  </label>
                </div>
              </div>
            </div>
            @if (hasAccessibilityStatement) {
              <app-common-input
                class="d-block margin-bottom"
                [formValidationRequest$]="formValidationRequest$"
                [form]="form"
                [useDefaultAsteriskColor]="digitalAssetFormUpdateFeatureEnabled"
                [context]="{
                  label: 'digital_accessibility_statement_label',
                  field: $digitalProperty.accessibilityStatement,
                  disabled: canEditProperty === false,
                }"
              ></app-common-input>
            }
            @if (digitalAssetsPrivacySettingFeatureEnabled) {
              <app-confidential-scanning-switch
                [value]="confidentialScanningEnabled"
                [disabled]="confidentialScanningEnabledTenantLevel"
                [tooltip]="confidentialScanningTooltip"
                (valueChange)="updateConfidentialScanningEnabled($event)"
              ></app-confidential-scanning-switch>
            }
            <app-scanning-agent-switch
              [value]="scanningAgentEnabled"
              (valueChange)="updateScanningAgentEnabled($event)"
            ></app-scanning-agent-switch>
          </div>
        }
        @if (canRenderMobileBundleId) {
          <app-common-input
            class="d-block margin-bottom"
            [formValidationRequest$]="formValidationRequest$"
            [form]="form"
            [useDefaultAsteriskColor]="digitalAssetFormUpdateFeatureEnabled"
            [context]="{
              label: 'mobile_bundle_id_label',
              field: $digitalProperty.mobileBundleId,
              description: 'mobile_bundle_id_description',
              disabled: !!digitalProperty?.[$digitalProperty.mobileBundleId],
            }"
          ></app-common-input>
        }
        @if (isMobile && isAdminView && (canUpdateDigitalProperties$ | async)) {
          <div class="row form-row">
            <div class="col-12 d-flex align-items-center">
              <input
                type="checkbox"
                id="noBundleIdRestriction"
                name="noBundleIdRestriction"
                [formControlName]="$digitalProperty.noBundleIdRestriction"
              />
              <label for="noBundleIdRestriction" class="label font-normal ms-2">
                {{ 'no_bundle_id_restriction' | translate }}
              </label>
            </div>
          </div>
        }
        @if (isMobile && digitalAssetsPrivacySettingFeatureEnabled) {
          <app-confidential-scanning-switch
            [value]="confidentialScanningEnabled"
            [disabled]="confidentialScanningEnabledTenantLevel"
            [tooltip]="confidentialScanningTooltip"
            (valueChange)="updateConfidentialScanningEnabled($event)"
          ></app-confidential-scanning-switch>
        }
      </fieldset>
    </form>
  }
</div>

<ng-template #domainRestrictionSettings let-form="form">
  @if (canRenderDomainRestrictionFields()) {
    <ng-container [formGroup]="form">
      <div class="row form-row" [hidden]="!isDomainRestrictionSupportedByDpType()">
        <div class="col-12">
          <div class="row">
            <div class="col-12">
              <label class="label mb-1" for="noDomainRestrictions">{{ 'digital_restrictions_label' | translate }}</label>
            </div>
          </div>
          <div class="row">
            <div class="col-12 d-flex align-items-center">
              <input
                type="checkbox"
                id="noDomainRestrictions"
                name="noDomainRestrictions"
                [formControlName]="$digitalProperty.noDomainRestrictions"
              />
              <label for="noDomainRestrictions" class="label font-normal ms-2">
                {{ 'digital_property_no_domain_restriction' | translate }}
              </label>
            </div>
          </div>
        </div>
      </div>
      <div class="row form-row" [hidden]="!isDomainRestrictionSupportedByDpType()">
        <div class="col-12">
          <div class="row">
            <div class="col-12">
              <label for="authorizedDomains">{{ 'label_authorized_domains' | translate }}</label>
              <svg
                viewBox="0 0 8 8"
                appTooltip="{{ 'authorized_domains_description' | translate }}"
                class="open-icon open-icon-primary margin-left-xs"
              >
                <use xlink:href="#question-mark-2"></use>
              </svg>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <textarea
                placeholder="{{ 'placeholder_authorized_domains' | translate }}"
                id="authorizedDomains"
                name="authorizedDomains"
                rows="5"
                [formControlName]="$digitalProperty.authorizedDomains"
                class="form-element w-100"
              ></textarea>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <div [id]="$digitalProperty.authorizedDomains + '_error'" class="error-container">
                @if (form.disabled === false) {
                  <app-form-field-error
                    [formValidationRequest$]="formValidationRequest$"
                    [field]="form.controls[$digitalProperty.authorizedDomains]"
                    [name]="$digitalProperty.authorizedDomains"
                    [label]="'label_authorized_domains'"
                  ></app-form-field-error>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </ng-container>
  }
</ng-template>
